// Client side of the admin analytics surface. Types mirror the
// /api/admin/analytics response (functions/utils/supabase.ts), plus the pure
// derivations (activity windows, version distribution) and a browser presence
// observer for the live "online now" count. The fetch is owner-gated server
// side; this module never touches a service-role key.

import { supabase } from './supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

// ─── Shapes returned by /api/admin/analytics ────────────────────────────────

export interface AdminUserStats {
  channel_points_collected: number;
  hours_watched: number;
  messages_sent: number;
  streams_watched: number;
  updated_at: string;
}

export interface Membership {
  status: 'active' | 'past_due' | 'canceled' | 'inactive';
  source: 'stripe' | 'paypal' | 'gift' | null;
  is_active: boolean;
  total_months: number;
  /** First-ever paid month, the lifetime "member since" anchor. */
  first_subscribed_at: string | null;
  /** When the current Stripe subscription started; null for gifts / no sub. */
  started_at: string | null;
  /** When cancellation was requested on Stripe; set even while the sub runs
   *  out its paid period, cleared if the member resumes. */
  canceled_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  /** Months still to accrue on a gift; null when permanent OR not a gift OR
   *  backed by a boost (read gift_source before treating null as permanent). */
  gift_months_remaining: number | null;
  /** Which kind of comp backs a 'gift' source: an admin grant, or a live
   *  Discord boost that lapses when the boost stops. Null when not comped. */
  gift_source: 'admin' | 'boost' | null;
  owns_subscriber_badge: boolean;
  owns_supporter_badge: boolean;
}

export interface AdminUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  last_seen: string;
  created_at: string;
  app_version: string | null;
  stats: AdminUserStats | null;
  membership: Membership;
  /** Non-Twitch platforms connected in the desktop app, e.g. ['kick']. Empty
   *  for members on Twitch alone, and for everyone until the desktop release
   *  that reports it has gone out. */
  linked_platforms: string[];
  /** StreamNook clients this member has signed in from, e.g. ['desktop',
   *  'android']. Persisted, so it stays correct while they are offline. */
  clients: string[];
  /** Stored accolade ids this member has earned (seasonal, cake day, limited
   *  events). Names come from src/lib/accolades.ts; the desktop's threshold
   *  accolades are derived from stats and never stored, so they are not here. */
  accolades?: string[];
  /** One entry per client, with what that client reports about itself. Prefer
   *  this over `app_version` for anything per-platform: `app_version` is one
   *  column shared by desktop 8.x and Android 0.1.x, so a member who uses both
   *  flip-flops to whichever signed in last. Empty until the heartbeat ships. */
  client_details?: AdminClientDetail[];
}

/** What one client of one member reports about itself.
 *
 *  Mirrors `AdminClientDetail` in `functions/utils/supabase.ts`. Both ends need
 *  the field or it never reaches the page: this file is the admin panel's OWN
 *  copy of the types, and adding a field to the API alone does nothing. That has
 *  caught people here before (the Android presence mark).
 *
 *  Null on `update_last_check_at` is a finding, not missing data: no update check
 *  has completed on that client. */
export interface AdminClientDetail {
  platform: string;
  arch: string | null;
  channel: string | null;
  version: string | null;
  last_seen: string | null;
  update_last_check_at: string | null;
  update_last_check_ok: boolean | null;
  update_artifact_for_platform: boolean | null;
  update_available: boolean | null;
  update_offered_version: string | null;
  update_last_error: string | null;
  report_failures: number | null;
}

/** Adoption of one non-Twitch platform. The ever/now pair reads as retention:
 *  one number alone would hide everyone who tried it and disconnected. */
export interface LinkedAccountTotals {
  provider: string;
  connected_now: number;
  ever_connected: number;
}

/** Term options for gifting a complimentary membership. null = permanent. */
export type GiftTerm = 1 | 3 | 6 | 12 | null;

export interface AdminGlobalStats {
  total_channel_points: number;
  total_hours_watched: number;
  total_messages_sent: number;
  total_streams_watched: number;
}

export interface DailyMetric {
  day: string; // YYYY-MM-DD (UTC)
  total_users: number;
  dau: number;
  wau: number;
  mau: number;
  new_users_24h: number;
  total_messages: number;
  total_hours: number;
  total_points: number;
  total_streams: number;
  /** Version histogram for that day. The rollup has written this daily since the
   *  daily_metrics migration; nothing selected it until 2026-09-19, so months of
   *  adoption history sat unread. Optional: older rows and a pre-migration DB
   *  legitimately have nothing here. */
  version_distribution?: Record<string, number> | null;
}

export interface AdminAnalytics {
  users: AdminUser[];
  global_stats: AdminGlobalStats;
  /** Daily rollup, ascending by day (oldest first), up to 90 days. */
  series: DailyMetric[];
  /** Per-platform account adoption. Empty until the user_linked_accounts
   *  migration has run. */
  linked_accounts: LinkedAccountTotals[];
  /** Who is a member and why, plus gross money. Null when its read failed;
   *  absent from a deployment older than the breakdown. */
  membership_summary?: MembershipSummary | null;
  generated_at: string;
  truncated: boolean;
}

// Mirrors functions/utils/adminMembership.ts.
export interface MoneyTotals {
  total_cents: number;
  subscriptions_cents: number;
  one_time_cents: number;
  gifts_cents: number;
  stripe_cents: number;
  paypal_cents: number;
  kofi_cents: number;
  payments: number;
}

export interface MembershipSummary {
  members: {
    total: number;
    paid: number;
    paid_monthly: number;
    paid_yearly: number;
    paid_stripe: number;
    paid_paypal: number;
    ending: number;
    past_due: number;
    boosting: number;
    boost_only: number;
    gift_only: number;
    duplicate_ids: string[];
  };
  money: {
    mrr_cents: number;
    mrr_ending_cents: number;
    this_month: MoneyTotals;
    last_30_days: MoneyTotals;
    all_time: MoneyTotals;
    months: Array<{ month: string; subscriptions_cents: number; one_time_cents: number; gifts_cents: number }>;
  };
}

export type AdminFetchError = 'unauthenticated' | 'forbidden' | 'failed';

export async function fetchAdminAnalytics(): Promise<AdminAnalytics> {
  const res = await fetch('/api/admin/analytics', { credentials: 'include' });
  if (res.status === 401) throw new Error('unauthenticated' satisfies AdminFetchError);
  if (res.status === 403) throw new Error('forbidden' satisfies AdminFetchError);
  if (!res.ok) throw new Error('failed' satisfies AdminFetchError);
  return (await res.json()) as AdminAnalytics;
}

// ─── Membership commands ─────────────────────────────────────────────────────
// Both POST to /api/admin/membership (owner-gated server-side) and return the
// recomputed membership for the affected user so the caller can patch its row.

interface MembershipActionResponse {
  twitch_user_id: string;
  membership: Membership;
}

async function postMembership(body: Record<string, unknown>): Promise<MembershipActionResponse> {
  const res = await fetch('/api/admin/membership', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => null) as { detail?: string } | null;
    throw new Error(detail?.detail ? `membership_failed:${detail.detail}` : `membership_failed:${res.status}`);
  }
  return (await res.json()) as MembershipActionResponse;
}

export function giftMembership(twitchUserId: string, termMonths: GiftTerm): Promise<MembershipActionResponse> {
  return postMembership({ action: 'gift', twitch_user_id: twitchUserId, term_months: termMonths });
}

export function revokeMembership(twitchUserId: string): Promise<MembershipActionResponse> {
  return postMembership({ action: 'revoke', twitch_user_id: twitchUserId });
}

// ─── Derived analytics (ported, snapshot-only until the daily rollup lands) ──

export interface ActivitySummary {
  /** Active in the last hour. */
  activeLastHour: number;
  /** Active in the last 24h. */
  dau: number;
  /** Active in the last 7 days. */
  wau: number;
  /** Active in the last 30 days. */
  mau: number;
  /** Signed up in the last 24h. */
  newToday: number;
  /** Signed up in the last 7 days. */
  newThisWeek: number;
}

export function computeActivitySummary(users: AdminUser[]): ActivitySummary {
  const now = Date.now();
  const hour = 3_600_000;
  const day = 24 * hour;

  let activeLastHour = 0;
  let dau = 0;
  let wau = 0;
  let mau = 0;
  let newToday = 0;
  let newThisWeek = 0;

  for (const u of users) {
    const seenAgo = now - new Date(u.last_seen).getTime();
    const createdAgo = now - new Date(u.created_at).getTime();
    if (seenAgo <= hour) activeLastHour++;
    if (seenAgo <= day) dau++;
    if (seenAgo <= 7 * day) wau++;
    if (seenAgo <= 30 * day) mau++;
    if (createdAgo <= day) newToday++;
    if (createdAgo <= 7 * day) newThisWeek++;
  }

  return { activeLastHour, dau, wau, mau, newToday, newThisWeek };
}

export interface VersionRow {
  version: string;
  count: number;
}

/** Version buckets for one platform, plus what "latest" means for it.
 *
 *  Per platform because desktop and Android are separate number spaces: pooling
 *  them put every desktop 8.x above every Android 0.1.x in one descending sort,
 *  and the panel renders a slice of the top rows, so Android fell off the list
 *  entirely once enough desktop versions existed. */
export interface PlatformVersions {
  /** 'windows' | 'macos' | 'linux' | 'android' | 'ios' | 'desktop' (legacy). */
  platform: string;
  rows: VersionRow[];
  /** Total clients counted for this platform. */
  total: number;
  /** Highest version seen on this platform, which stands in for "latest": we do
   *  not know the release list per platform here, and the newest build in the
   *  wild is the useful comparison anyway. Null when nothing reports a version. */
  newest: string | null;
  /** Clients on `newest`. */
  onNewest: number;
}

/** Normalize a version string into a histogram key.
 *
 *  Trim, strip a leading `v`, lowercase. Without this, `v8.6.4`, `8.6.4 ` and
 *  `8.6.4` were three separate buckets. Empty and whitespace-only collapse to
 *  `unknown`, which is also what a real null becomes. */
function versionKey(raw: string | null | undefined): string {
  const s = (raw ?? '').trim().replace(/^v/i, '').toLowerCase();
  return s || 'unknown';
}

/** Descending version order with `unknown` pinned last. `numeric` handles the
 *  two-digit patch case, so 8.6.10 correctly sorts above 8.6.4. */
function byVersionDesc(a: VersionRow, b: VersionRow): number {
  if (a.version === 'unknown') return 1;
  if (b.version === 'unknown') return -1;
  return b.version.localeCompare(a.version, undefined, { numeric: true });
}

/**
 * Flat version histogram across every member, from `users.app_version`.
 *
 * Kept for the "all accounts" view, but prefer `computePlatformVersions` for
 * anything you intend to draw conclusions from: this column mixes desktop and
 * Android, and is written once per login so churned accounts stay frozen at
 * whatever they last ran, permanently inflating the old buckets.
 */
export function computeVersionDistribution(users: AdminUser[]): VersionRow[] {
  const counts = new Map<string, number>();
  for (const u of users) {
    const v = versionKey(u.app_version);
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([version, count]) => ({ version, count }))
    .sort(byVersionDesc);
}

/** Whether a `user_clients` platform is an installed app. The `web` row records
 *  website sign-ins and has no version or updater, so version adoption and
 *  update health leave it out. */
export function isAppClient(platform: string): boolean {
  return platform !== 'web';
}

/**
 * Version histogram per platform, from `user_clients`.
 *
 * This is the one that can answer "is macOS stuck": one row per (member,
 * platform), each carrying that client's own version, so a member who uses
 * Windows and a phone contributes to both instead of flip-flopping.
 *
 * `activeWithinDays` defaults to 30. An unbounded histogram describes "every
 * account that ever existed", not the installed base, and since a client's
 * version is never cleared, accounts that churned two years ago sit in the old
 * buckets forever.
 */
export function computePlatformVersions(
  users: AdminUser[],
  activeWithinDays: number | null = 30,
): PlatformVersions[] {
  const cutoff =
    activeWithinDays === null ? null : Date.now() - activeWithinDays * 24 * 60 * 60 * 1000;

  const byPlatform = new Map<string, Map<string, number>>();
  for (const u of users) {
    for (const c of u.client_details ?? []) {
      if (!isAppClient(c.platform)) continue;
      if (cutoff !== null) {
        // Fall back to the member's own last_seen when the client row predates
        // last_seen being written per client, so an old row is filtered on
        // something rather than silently always included.
        const seen = Date.parse(c.last_seen ?? u.last_seen);
        if (Number.isNaN(seen) || seen < cutoff) continue;
      }
      const bucket = byPlatform.get(c.platform) ?? new Map<string, number>();
      const key = versionKey(c.version);
      bucket.set(key, (bucket.get(key) ?? 0) + 1);
      byPlatform.set(c.platform, bucket);
    }
  }

  return Array.from(byPlatform.entries())
    .map(([platform, counts]) => {
      const rows = Array.from(counts.entries())
        .map(([version, count]) => ({ version, count }))
        .sort(byVersionDesc);
      const total = rows.reduce((n, r) => n + r.count, 0);
      const newestRow = rows.find((r) => r.version !== 'unknown') ?? null;
      return {
        platform,
        rows,
        total,
        newest: newestRow?.version ?? null,
        onNewest: newestRow?.count ?? 0,
      };
    })
    // Biggest platform first: that is the one worth looking at.
    .sort((a, b) => b.total - a.total);
}

/** Why the clients that are behind are behind, for one cohort.
 *
 *  Every field is a count of CLIENTS, not members, because the question is about
 *  installs. The categories are deliberately not mutually exclusive: a client can
 *  be failing its checks AND have an old version. */
export interface UpdateHealth {
  /** Clients counted (active within the window, and reporting a heartbeat). */
  clients: number;
  /** No update check has ever completed on this client. */
  neverChecked: number;
  /** The last check failed. They are trying and cannot get through. */
  checkFailing: number;
  /** The manifest publishes no build for their os-arch. Checking fine, offered
   *  nothing. This is the macOS-mid-release and the Linux case. */
  notPublishedFor: number;
  /** An update WAS offered and they are still on the old build. A product
   *  problem, not a delivery one, and the two need separating. */
  offeredAndIgnored: number;
  /** Reported after one or more failed heartbeats, i.e. was invisible for a
   *  while. Non-zero here means the fleet numbers were under-counting. */
  recoveredReporters: number;
  /** Clients with no health fields at all: too old to report, or the migration
   *  has not been applied. */
  notReporting: number;
}

/**
 * Adoption curve for the newest release, from the daily rollup's stored histogram.
 *
 * "Newest" is resolved ONCE, from the most recent day that has a histogram, and
 * then tracked backwards. Picking the max version per day instead would plot a
 * line that is 100% every day by construction, since whatever is newest that day
 * is the one being measured.
 *
 * Returns percentages, because the account total grows and a raw count going up
 * cannot be told apart from signups. Empty when no day carries a histogram, which
 * is the honest answer for a DB where the rollup never ran.
 */
export function computeAdoptionCurve(series: DailyMetric[]): {
  version: string | null;
  points: number[];
} {
  const withHistogram = series.filter(
    (d) => d.version_distribution && Object.keys(d.version_distribution).length > 0,
  );
  if (withHistogram.length === 0) return { version: null, points: [] };

  const latest = withHistogram[withHistogram.length - 1].version_distribution!;
  const newest = Object.keys(latest)
    .map(versionKey)
    .filter((v) => v !== 'unknown')
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))[0];
  if (!newest) return { version: null, points: [] };

  const points = withHistogram.map((d) => {
    const hist = d.version_distribution!;
    let total = 0;
    let onNewest = 0;
    for (const [raw, n] of Object.entries(hist)) {
      const count = Number(n) || 0;
      total += count;
      if (versionKey(raw) === newest) onNewest += count;
    }
    return total > 0 ? (onNewest / total) * 100 : 0;
  });

  return { version: newest, points };
}

export function computeUpdateHealth(
  users: AdminUser[],
  activeWithinDays: number = 7,
): UpdateHealth {
  const cutoff = Date.now() - activeWithinDays * 24 * 60 * 60 * 1000;
  const h: UpdateHealth = {
    clients: 0,
    neverChecked: 0,
    checkFailing: 0,
    notPublishedFor: 0,
    offeredAndIgnored: 0,
    recoveredReporters: 0,
    notReporting: 0,
  };

  for (const u of users) {
    for (const c of u.client_details ?? []) {
      if (!isAppClient(c.platform)) continue;
      const seen = Date.parse(c.last_seen ?? u.last_seen);
      if (Number.isNaN(seen) || seen < cutoff) continue;
      h.clients++;

      // A client that reports nothing at all is its own category: counting it as
      // "never checked" would blame the updater for a client too old to speak.
      const reports = c.arch !== null || c.channel !== null || c.update_last_check_at !== null;
      if (!reports) {
        h.notReporting++;
        continue;
      }

      if (c.update_last_check_at === null) h.neverChecked++;
      if (c.update_last_check_ok === false) h.checkFailing++;
      if (c.update_artifact_for_platform === false) h.notPublishedFor++;
      if (c.update_available === true) h.offeredAndIgnored++;
      if ((c.report_failures ?? 0) > 0) h.recoveredReporters++;
    }
  }

  return h;
}

// ─── Live presence (browser observer) ───────────────────────────────────────
// Subscribes to the same global-presence channel the desktop app tracks into,
// but never calls track(), so the web admin is an invisible observer and does
// not inflate the count it is reading.

interface PresencePayload {
  user_id?: string;
  display_name?: string;
  app_version?: string;
  /** android | desktop, sent by the client next to app_version. Clients
   *  older than the Android beta do not send it, so absent means UNKNOWN - it
   *  is fine to render nothing, but never read it as proof of desktop. */
  platform?: string;
  online_at: string;
}

// Presence carries ONLY liveness. Everything descriptive was removed on purpose
// (H14): the payload is a websocket write governed by no RLS policy, so anyone
// holding the publishable key can join `global-presence` under an arbitrary key
// and publish arbitrary display names, versions and platforms. Rendering those
// strings put attacker-controlled text straight into the admin dashboard.
//
// Name / platform / version now come from the SERVER-side admin directory
// (AdminUser, service-role) instead. Resolve by user_id at the call site.
//
// RESIDUAL, accepted: `user_id` itself is still client-supplied, so a forged
// entry can mark a REAL member as online. That cannot be closed without an
// authenticated realtime session, which does not exist yet. It is a much smaller
// lie than injected text, and the ids are now cross-referenced against the real
// directory, so a fabricated id resolves to nobody.
export interface OnlineUser {
  user_id: string;
  online_at: string;
}

export interface OnlinePresenceSnapshot {
  authedUsers: OnlineUser[];
  anonKeyCount: number;
  /** Unique participants = authed users + anonymous sessions. */
  totalUnique: number;
  ready: boolean;
}

export const emptyPresenceSnapshot = (): OnlinePresenceSnapshot => ({
  authedUsers: [],
  anonKeyCount: 0,
  totalUnique: 0,
  ready: false,
});

let presenceChannel: RealtimeChannel | null = null;
let presenceReady = false;
const presenceSubscribers = new Set<(snap: OnlinePresenceSnapshot) => void>();

const computeSnapshot = (): OnlinePresenceSnapshot => {
  if (!presenceChannel) return emptyPresenceSnapshot();
  const state = presenceChannel.presenceState<PresencePayload>();
  const byUserId = new Map<string, OnlineUser>();
  let anonKeyCount = 0;

  for (const key of Object.keys(state)) {
    const arr = state[key];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const fresh = arr.reduce((a, b) =>
      new Date(b.online_at).getTime() > new Date(a.online_at).getTime() ? b : a,
    );
    if (fresh.user_id) {
      const existing = byUserId.get(fresh.user_id);
      if (!existing || new Date(fresh.online_at).getTime() > new Date(existing.online_at).getTime()) {
        // Deliberately NOT copying display_name / app_version / platform.
        byUserId.set(fresh.user_id, {
          user_id: fresh.user_id,
          online_at: fresh.online_at,
        });
      }
    } else {
      anonKeyCount++;
    }
  }

  return {
    authedUsers: Array.from(byUserId.values()).sort(
      (a, b) => new Date(b.online_at).getTime() - new Date(a.online_at).getTime(),
    ),
    anonKeyCount,
    totalUnique: byUserId.size + anonKeyCount,
    ready: presenceReady,
  };
};

const notify = () => {
  if (presenceSubscribers.size === 0) return;
  const snap = computeSnapshot();
  for (const cb of presenceSubscribers) {
    try { cb(snap); } catch (e) { console.error('[admin] presence subscriber error', e); }
  }
};

const ensureChannel = () => {
  if (!supabase || presenceChannel) return;
  presenceChannel = supabase.channel('global-presence', {
    config: { presence: { key: `web_admin_observer_${Math.random().toString(36).slice(2)}` } },
  });
  presenceChannel
    .on('presence', { event: 'sync' }, () => { presenceReady = true; notify(); })
    .on('presence', { event: 'join' }, () => notify())
    .on('presence', { event: 'leave' }, () => notify())
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') notify(); // never track() — observer only
    });
};

export function subscribeToOnlinePresence(
  callback: (snap: OnlinePresenceSnapshot) => void,
): () => void {
  presenceSubscribers.add(callback);
  ensureChannel();
  try { callback(computeSnapshot()); } catch (e) { console.error('[admin] presence subscriber error', e); }
  return () => {
    presenceSubscribers.delete(callback);
    if (presenceSubscribers.size === 0 && presenceChannel) {
      presenceChannel.unsubscribe();
      presenceChannel = null;
      presenceReady = false;
    }
  };
}
